import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { normalizeGameType } from '../config/gameTypes';
import { getConfig } from '../config/environment';
import { hasAuthorizedContentAdminToken } from '../utils/adminToken';

const LayoutStyleContext = createContext(undefined);

const isAdminSession = () => hasAuthorizedContentAdminToken(getConfig().tlfId);
const siteConfigLayoutStyle = () => window?.SITE_CONFIG?.defaults?.layout_style || 'style1';

const CHARITY_SHOWCASE_STYLE = { id: 'charity', name: '❤️ Charity Showcase', description: 'Puts your organization front and center' };

const LEGACY_V2_STYLE = { id: 'v2', name: '🔄 Legacy V2', description: 'Classic V1/V2 legacy layout' };

const CINEMATIC_STYLE = { id: 'cinematic', name: '🎬 Cinematic', description: 'Full-viewport fixed background with dynamic overlays — dramatic, immersive hero' };

const FLUID_STYLE = { id: 'fluid', name: '🪄 Fluid Stack', description: 'Single-column — every block is a reorderable section' };
const PREMIUM_STYLE = { id: 'premium', name: '✨ Premium', description: 'Modern premium layout with animations' };
const PREMIUM_PLUS_STYLE = { id: 'premium-plus', name: '🎨 Premium +', description: 'Theme-driven gradient hero that adapts to your charity colors' };

const DEFAULT_CLASSIC_STYLES = [
  PREMIUM_STYLE,
  PREMIUM_PLUS_STYLE,
  { id: 'style1', name: 'Modern Grid', description: 'Clean grid layout with hero carousel' },
  { id: 'style2', name: 'Split View', description: 'Side-by-side content and draw info' },
  { id: 'style3', name: 'Card Focus', description: 'Card-based layout with featured sections' },
  { id: 'style4', name: 'Game Show', description: 'Vegas-style layout with animated jackpot display' },
  { id: 'style5', name: 'Spotlight', description: 'Full-width cinematic layout with dramatic effects' },
  { id: 'standard', name: 'Classic', description: 'Traditional charity lottery layout' },
  CINEMATIC_STYLE,
  FLUID_STYLE,
  LEGACY_V2_STYLE,
  CHARITY_SHOWCASE_STYLE,
];

const DEFAULT_MODIFIED_STYLES = [
  PREMIUM_STYLE,
  PREMIUM_PLUS_STYLE,
  { id: 'style1', name: 'Modern Grid', description: 'Clean grid layout with hero carousel' },
  { id: 'style2', name: 'Split View', description: 'Side-by-side content and draw info' },
  { id: 'style3', name: 'Card Focus', description: 'Card-based layout with featured sections' },
  { id: 'standard', name: 'Classic', description: 'Traditional charity lottery layout' },
  CINEMATIC_STYLE,
  FLUID_STYLE,
  LEGACY_V2_STYLE,
  CHARITY_SHOWCASE_STYLE,
];

const DEFAULT_PROGRESSIVE_STYLES= [
  PREMIUM_PLUS_STYLE,
  PREMIUM_STYLE,
  { id: 'style2', name: 'Progressive Modern', description: 'Modern progressive layout' },
  { id: 'style3', name: 'Momentum Meter', description: 'Arc meter hero + 8-week sparkline trajectory' },
  { id: 'standard', name: 'Standard', description: 'Clean basic layout' },
  CINEMATIC_STYLE,
  FLUID_STYLE,
  LEGACY_V2_STYLE,
  CHARITY_SHOWCASE_STYLE,
];

const DEFAULT_CHASE_STYLES = [
  { id: 'style1', name: '✨ Premium', description: 'Modern card game layout with animations' },
  { id: 'deckRunner', name: 'Deck Runner', description: 'Playing-card visual with deck spread hero and drawn-cards row' },
  { id: 'storybook', name: '📖 Storybook', description: 'Cosmic steampunk hero with ornate plaque, scroll banner & wooden ledge' },
  { id: 'standard', name: 'Standard', description: 'Clean basic layout' },
  CINEMATIC_STYLE,
  FLUID_STYLE,
  LEGACY_V2_STYLE,
  CHARITY_SHOWCASE_STYLE,
];

const DEFAULT_ELIMINATION_STYLES= [
  { id: 'style1', name: 'Inferno', description: 'High-energy red-orange elimination layout with progress tracker' },
  { id: 'style2', name: 'Tournament Bracket', description: 'SVG bracket tree — entries collapse round-by-round to the final' },
  { id: 'standard', name: 'Standard', description: 'Clean basic layout' },
  CINEMATIC_STYLE,
  FLUID_STYLE,
  LEGACY_V2_STYLE,
  CHARITY_SHOWCASE_STYLE,
];

const DEFAULT_SQUARE_STYLES = [
  { id: 'standard', name: 'Standard',  description: 'Grid with sidebar info' },
  { id: 'gameday',  name: 'Scoreboard', description: 'Stadium scoreboard hero — dot-matrix jackpot, tile board, game clock' },
  PREMIUM_STYLE,
  CINEMATIC_STYLE,
  FLUID_STYLE,
  LEGACY_V2_STYLE,
  CHARITY_SHOWCASE_STYLE,
];

const DEFAULT_PRIZE_DRAW_STYLES = [
  { id: 'style1', name: 'Classic',  description: 'Standard prize draw layout' },
  { id: 'style2', name: 'Prize Gallery Spotlight',  description: 'Cinematic prize carousel with thumbnail strip' },
  { id: 'standard', name: 'Standard', description: 'Clean basic layout' },
  PREMIUM_STYLE,
  CINEMATIC_STYLE,
  FLUID_STYLE,
  LEGACY_V2_STYLE,
  CHARITY_SHOWCASE_STYLE,
];

const DEFAULT_SUPERBALL_BINGO_STYLES = [
  { id: 'standard', name: 'Standard', description: 'Clean basic layout' },
  { id: 'style1', name: 'Bingo Hall', description: 'Classic bingo hall aesthetic with warm colors' },
  { id: 'style2', name: 'Casino',     description: 'Modern dark theme with neon accents' },
  { id: 'style3', name: 'Clean',      description: 'Minimal, accessible design' },
  { id: 'liveBoard', name: 'Live Board', description: 'Full 75-number board grid with live-call glow effects' },
  PREMIUM_STYLE,
  CINEMATIC_STYLE,
  FLUID_STYLE,
  LEGACY_V2_STYLE,
  CHARITY_SHOWCASE_STYLE,
];

export function LayoutStyleProvider({ children }) {
  const [layoutStyle, setLayoutStyle] = useState(() => {
    try {
      // Admin sessions use localStorage for draft preview persistence across navigation
      if (isAdminSession()) {
        const saved = localStorage.getItem('layoutStyle');
        if (saved) return saved;
      }
      // For all users, check the published layout cache to avoid FOUC
      const published = localStorage.getItem('publishedLayoutStyle');
      if (published) return published;
      return siteConfigLayoutStyle();
    } catch { return 'style1'; }
  });

  const [availableStyles, setAvailableStyles] = useState([]);

  const loadLayoutStyles = useCallback((gameType) => {
    let styles;
    if (gameType === 'eliminationDraw') {
      styles = DEFAULT_ELIMINATION_STYLES;
    } else if (gameType === 'squareGame') {
      styles = DEFAULT_SQUARE_STYLES;
    } else if (gameType === 'prizeDraw') {
      styles = DEFAULT_PRIZE_DRAW_STYLES;
    } else if (gameType === 'progressive5050') {
      styles = DEFAULT_PROGRESSIVE_STYLES;
    } else if (gameType === 'chaseTheAce') {
      styles = DEFAULT_CHASE_STYLES;
    } else if (gameType === 'superballBingo') {
      styles = DEFAULT_SUPERBALL_BINGO_STYLES;
    } else if (gameType === 'classic5050') {
      styles = DEFAULT_CLASSIC_STYLES;
    } else if (gameType === 'modified5050') {
      styles = DEFAULT_MODIFIED_STYLES;
    } else if (window.SITE_CONFIG?.layout_styles) {
      styles = Object.values(window.SITE_CONFIG.layout_styles);
    } else {
      styles = DEFAULT_CLASSIC_STYLES;
    }
    setAvailableStyles(styles);
  }, []);

  // Initialise on mount from SITE_CONFIG or stored gameType
  useEffect(() => {
    const siteGameType = window?.SITE_CONFIG?.defaults?.game_type || window?.SITE_CONFIG?.game_type?.id;
    const normalized = normalizeGameType(siteGameType) || normalizeGameType(localStorage.getItem('gameType')) || 'classic5050';
    loadLayoutStyles(normalized);
  }, [loadLayoutStyles]);

  // Validate current selection whenever available styles change
  useEffect(() => {
    if (availableStyles.length === 0) return;
    // After publish/discard reset, trust the server value — don't override it
    if (isResettingRef.current) {
      isResettingRef.current = false;
      return;
    }
    const validIds = availableStyles.map(s => s.id);
    if (!validIds.includes(layoutStyle)) {
      const fallback = validIds.includes('standard') ? 'standard' : validIds[0];
      setLayoutStyle(fallback);
      if (isAdminSession()) {
        try { localStorage.setItem('layoutStyle', fallback); } catch {}
      }
    }
  }, [availableStyles, layoutStyle]);

  // Only persist to localStorage during admin sessions
  useEffect(() => {
    if (isAdminSession()) {
      try { localStorage.setItem('layoutStyle', layoutStyle); } catch {}
    }
  }, [layoutStyle]);

  // Listen for publish/discard to reset to server values
  const isResettingRef = React.useRef(false);
  useEffect(() => {
    const handleReset = () => {
      try { localStorage.removeItem('layoutStyle'); } catch {}
      isResettingRef.current = true;
      // Use the server value; fall back to the published cache if SITE_CONFIG isn't updated yet
      const serverValue = siteConfigLayoutStyle();
      const cached = (() => { try { return localStorage.getItem('publishedLayoutStyle'); } catch { return null; } })();
      const resolved = (serverValue && serverValue !== 'style1') ? serverValue : (cached || serverValue);
      setLayoutStyle(resolved);
    };
    window.addEventListener('appearanceReset', handleReset);
    return () => window.removeEventListener('appearanceReset', handleReset);
  }, []);

  // Sync from DraftContext appearance changes (Draft is a child provider; use event bus).
  // Allows fresh tabs / cross-tab sync / preview page to reflect the saved draft layout.
  useEffect(() => {
    const handleDraftSync = (event) => {
      if (!isAdminSession()) return;
      const next = event?.detail?.appearance?.layoutStyle;
      if (typeof next === 'string' && next && next !== layoutStyle) {
        try { localStorage.setItem('layoutStyle', next); } catch {}
        setLayoutStyle(next);
      }
    };
    window.addEventListener('appearanceDraftSync', handleDraftSync);
    return () => window.removeEventListener('appearanceDraftSync', handleDraftSync);
  }, [layoutStyle]);

  const changeLayoutStyle = (style) => {
    if (!style || style === layoutStyle) return;
    if (isAdminSession()) {
      try { localStorage.setItem('layoutStyle', style); } catch {}
    }
    // Cache published layout for all users to prevent FOUC on next visit
    try { localStorage.setItem('publishedLayoutStyle', style); } catch {}
    setLayoutStyle(style);
  };

  const contextValue = {
    layoutStyle,
    changeLayoutStyle,
    availableStyles,
    loadLayoutStyles,   // exposed so LayoutStyleDropdown can call it on game type change
  };

  return (
    <LayoutStyleContext.Provider value={contextValue}>
      {children}
    </LayoutStyleContext.Provider>
  );
}

export function useLayoutStyle() {
  const context = useContext(LayoutStyleContext);
  if (context === undefined) {
    throw new Error('useLayoutStyle must be used within a LayoutStyleProvider');
  }
  return context;
}
